import { Article } from '@wix/answers-api';
import { FloatingNotification } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { StatusWarning as StatusWarningIcon } from '@wix/wix-ui-icons-common';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { DATA_HOOKS } from '../../dataHooks';
import { AB_TESTING_LABELS } from '../../types';
import { useArticlePageBi } from '../../utils/article-page-bi';
import { useStore } from '../../zustand/stores';

type ArticleABTestNotificationProps = {
  article: Article;
};

export const ArticleABTestNotification: FunctionComponent<
  ArticleABTestNotificationProps
> = ({ article }) => {
  const { isWixEmployee } = useStore((state) => state.userSlice);
  const [isDisplay, setIsDisplay] = useState(
    isWixEmployee && article.abTestInfo?.experimentId
  );
  const {
    locale,
    query: { alternativeContent },
  } = useRouter();
  const { t } = useTranslation();

  const { articleIsBeingAbTestedView, articleIsBeingAbTestedClick } =
    useArticlePageBi(article);

  useEffect(() => {
    void (async () => {
      if (isDisplay) {
        const labelText = t('article.ab-test-notification', {
          version:
            alternativeContent === 'true'
              ? AB_TESTING_LABELS.B
              : AB_TESTING_LABELS.A,
        });
        const ctaText = t('article.ab-test-notification-open-version', {
          version:
            alternativeContent === 'true'
              ? AB_TESTING_LABELS.A
              : AB_TESTING_LABELS.B,
        });
        const text = `${labelText} ${ctaText}`;
        await articleIsBeingAbTestedView(text);
      }
    })();
  }, [isDisplay]);

  useEffect(() => {
    if (isDisplay) {
      document.body.classList.add('article-ab-test-notification-visible');
    } else {
      document.body.classList.remove('article-ab-test-notification-visible');
    }
  }, [isDisplay]);

  const abTestNotificationClick = async () => {
    const labelText = t('article.ab-test-notification', {
      version: alternativeContent ? AB_TESTING_LABELS.B : AB_TESTING_LABELS.A,
    });
    const ctaText = t('article.ab-test-notification-open-version', {
      version: alternativeContent ? AB_TESTING_LABELS.A : AB_TESTING_LABELS.B,
    });
    const text = `${labelText} ${ctaText}`;

    await articleIsBeingAbTestedClick(
      text,
      ctaText,
      `${locale}${article.uri}?alternativeContent=${!alternativeContent}`
    );
  };

  return isDisplay ? (
    <FloatingNotification
      prefixIcon={<StatusWarningIcon />}
      dataHook={DATA_HOOKS.ARTICLE_AB_TEST_NOTIFICATION}
      type="warning"
      onClose={() => setIsDisplay(false)}
      text={t('article.ab-test-notification', {
        version: alternativeContent ? AB_TESTING_LABELS.B : AB_TESTING_LABELS.A,
      })}
      textButtonProps={{
        label: t('article.ab-test-notification-open-version', {
          version: alternativeContent
            ? AB_TESTING_LABELS.A
            : AB_TESTING_LABELS.B,
        }),
        as: 'a',
        onClick: abTestNotificationClick,
        href: `${locale}${
          article.uri
        }?alternativeContent=${!alternativeContent}`,
        target: '_blank',
      }}
      fullWidth
    />
  ) : null;
};
